import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { IoSend, IoTrashOutline, IoRefreshOutline } from 'react-icons/io5';
import './App.css';

// Markdown component đơn giản để thay thế react-markdown
const MarkdownRenderer = ({ children, isUser }) => {
  const formatText = (text) => {
    if (typeof text !== 'string') return text;
    
    // Split text into lines để xử lý từng dòng
    const lines = text.split('\n');
    let formatted = '';
    let inOrderedList = false;
    let inUnorderedList = false;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Check for ordered list items (1. 2. 3. etc.)
      const orderedMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
      if (orderedMatch) {
        const [, , , content] = orderedMatch;
        
        // Close any open unordered list
        if (inUnorderedList) {
          formatted += '</ul>';
          inUnorderedList = false;
        }
        
        // Start ordered list if not already started
        if (!inOrderedList) {
          formatted += '<ol>';
          inOrderedList = true;
        }
        
        // Process markdown in list content
        const processedContent = content
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
          .replace(/`([^`]+)`/g, '<code>$1</code>')
          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        
        formatted += `<li>${processedContent}</li>`;
        continue;
      }
      
      // Check for unordered list items (- * +)
      const unorderedMatch = line.match(/^(\s*)[-*+]\s+(.*)$/);
      if (unorderedMatch) {
        const [, , content] = unorderedMatch;
        
        // Close any open ordered list
        if (inOrderedList) {
          formatted += '</ol>';
          inOrderedList = false;
        }
        
        // Start unordered list if not already started
        if (!inUnorderedList) {
          formatted += '<ul>';
          inUnorderedList = true;
        }
        
        // Process markdown in list content
        const processedContent = content
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
          .replace(/`([^`]+)`/g, '<code>$1</code>')
          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        
        formatted += `<li>${processedContent}</li>`;
        continue;
      }
      
      // Close any open lists if this line is not a list item
      if (inOrderedList) {
        formatted += '</ol>';
        inOrderedList = false;
      }
      if (inUnorderedList) {
        formatted += '</ul>';
        inUnorderedList = false;
      }
      
      // Process other markdown elements
      let processedLine = line
        // Headers
        .replace(/^#### (.*)$/, '<h4>$1</h4>')
        .replace(/^### (.*)$/, '<h3>$1</h3>')
        .replace(/^## (.*)$/, '<h2>$1</h2>')
        .replace(/^# (.*)$/, '<h1>$1</h1>')
        // Bold and italic
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
        // Code blocks (multiline) - start
        .replace(/^```(.*)$/, '<pre><code>')
        // Inline code
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        // Blockquotes
        .replace(/^>\s+(.*)$/, '<blockquote>$1</blockquote>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      
      // Handle code block closing
      if (line.trim() === '```') {
        processedLine = '</code></pre>';
      }
      
      // Add line break if not empty and not already formatted
      if (processedLine.trim() !== '' && 
          !processedLine.startsWith('<h') && 
          !processedLine.startsWith('<blockquote') &&
          !processedLine.startsWith('<pre') &&
          !processedLine.endsWith('</pre>')) {
        formatted += processedLine + '<br/>';
      } else {
        formatted += processedLine;
      }
    }
    
    // Close any remaining open lists
    if (inOrderedList) {
      formatted += '</ol>';
    }
    if (inUnorderedList) {
      formatted += '</ul>';
    }
    
    // Handle code blocks that span multiple lines
    formatted = formatted.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
    
    return formatted;
  };

  return (
    <div 
      dangerouslySetInnerHTML={{ 
        __html: formatText(children) 
      }} 
    />
  );
};

const AppContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #F8FAFC;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
  
  /* Subtle pattern overlay for depth */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(124, 58, 237, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 40% 60%, rgba(30, 64, 175, 0.02) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
  
  /* All children above the pattern */
  > * {
    position: relative;
    z-index: 1;
  }
`;

const Header = styled.header`
  background: #FFFFFF;
  padding: 1rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06);
  position: relative;
  min-height: auto;
  border-bottom: 1px solid #E2E8F0;
  
  h1 {
    margin: 0;
    color: #333;
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.2;
    padding-right: 120px; /* Space for action buttons */
  }
  
  p {
    margin: 0.5rem 0 0 0;
    color: #666;
    font-size: 0.9rem;
    line-height: 1.3;
    padding-right: 120px; /* Space for action buttons */
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    
    h1 {
      font-size: 1.2rem;
      padding-right: 80px; /* Smaller space on mobile */
      margin-bottom: 0.25rem;
    }
    
    p {
      font-size: 0.8rem;
      padding-right: 80px;
      margin: 0;
      
      /* Hide processing time on very small screens */
      span {
        display: none;
      }
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    
    h1 {
      font-size: 1rem;
      padding-right: 70px;
    }
    
    p {
      font-size: 0.75rem;
      padding-right: 70px;
      /* Show abbreviated text on very small screens */
      &::after {
        content: "";
      }
    }
  }
`;

const HeaderActions = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    right: 0.75rem;
    gap: 0.25rem;
    top: 50%;
  }
  
  @media (max-width: 480px) {
    right: 0.5rem;
    gap: 0.15rem;
    top: 45%;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  background: rgba(59, 130, 246, 0.15);
  border: 2px solid transparent;
  border-radius: 0.5rem;
  color: #3B82F6;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: linear-gradient(135deg, #3B82F6, #1D4ED8);
    color: white;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem;
    min-width: 36px;
    min-height: 36px;
    border-radius: 0.4rem;
    
    svg {
      font-size: 1.1rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.3rem;
    min-width: 32px;
    min-height: 32px;
    border-radius: 0.3rem;
    
    svg {
      font-size: 1rem;
    }
    
    &:hover {
      transform: none; /* Disable hover effects on touch devices */
    }
  }
  
  /* Touch-friendly improvements */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      background: rgba(59, 130, 246, 0.15);
      color: #3B82F6;
      transform: none;
    }
    
    &:active {
      background: linear-gradient(135deg, #3B82F6, #1D4ED8);
      color: white;
      transform: scale(0.95);
    }
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1.5rem;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 1rem;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(59, 130, 246, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(59, 130, 246, 0.3);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(59, 130, 246, 0.5);
  }
`;

const Message = styled.div`
  max-width: 80%;
  padding: 1rem;
  border-radius: 1rem;
  word-wrap: break-word;
  animation: slideIn 0.3s ease-out;
  
  ${props => props.isUser ? `
    align-self: flex-end;
    background: linear-gradient(135deg, #3B82F6, #1E40AF);
    color: white;
    border-bottom-right-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  ` : `
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.95);
    color: #374151;
    border-bottom-left-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    border: 1px solid rgba(59, 130, 246, 0.2);
  `}
  
  /* Markdown styling */
  h1, h2, h3, h4, h5, h6 {
    margin: 0.5rem 0;
    color: ${props => props.isUser ? 'white' : '#1F2937'};
  }
  
  p {
    margin: 0.5rem 0;
    line-height: 1.5;
  }
  
  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  ol {
    list-style-type: decimal;
    counter-reset: item;
  }
  
  ul {
    list-style-type: disc;
  }
  
  li {
    margin: 0.25rem 0;
    display: list-item;
  }
  
  ol li {
    list-style-type: decimal;
  }
  
  ul li {
    list-style-type: disc;
  }
  
  strong {
    font-weight: 600;
  }
  
  em {
    font-style: italic;
  }
  
  code {
    background: ${props => props.isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }
  
  pre {
    background: ${props => props.isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 0.5rem 0;
    
    code {
      background: none;
      padding: 0;
    }
  }
  
  blockquote {
    border-left: 4px solid ${props => props.isUser ? 'rgba(255,255,255,0.5)' : '#3B82F6'};
    padding-left: 1rem;
    margin: 0.5rem 0;
    font-style: italic;
    background: ${props => props.isUser ? 'rgba(255,255,255,0.1)' : 'rgba(59, 130, 246, 0.1)'};
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  background: #FFFFFF;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  border: 1px solid #E2E8F0;
  transition: all 0.3s ease;
  
  ${props => props.isLoading && `
    border-color: #3B82F6;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1);
    
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      background: linear-gradient(45deg, #3B82F6, #1E40AF, #7C3AED);
      background-size: 200% 200%;
      border-radius: 1rem;
      z-index: -1;
      animation: pulseGradient 2s infinite;
      opacity: 0.1;
    }
  `}
  
  @keyframes pulseGradient {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }
  
  &:focus-within {
    border-color: #3B82F6;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0.75rem;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  background: #F8FAFC;
  color: #374151;
  
  &:focus {
    background: #F1F5F9;
  }
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &:disabled {
    background: #F1F5F9;
    color: #94A3B8;
  }
`;

const SendButton = styled.button`
  padding: 0.75rem;
  background: linear-gradient(135deg, #3B82F6, #1E40AF);
  color: white;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 3rem;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1E40AF, #1E3A8A);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    background: #D1D5DB;
    color: #9CA3AF;
    cursor: not-allowed;
    box-shadow: none;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const TypingIndicator = styled.div`
  align-self: flex-start;
  padding: 1rem;
  background: #FFFFFF;
  border-radius: 1rem;
  border-bottom-left-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.2);
  position: relative;
  
  .typing-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .dots {
    display: flex;
    gap: 0.25rem;
  }
  
  .dot {
    width: 8px;
    height: 8px;
    background: #3B82F6;
    border-radius: 50%;
    animation: typing 1.4s infinite;
  }
  
  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  
  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }
  
  .timer {
    font-size: 0.8rem;
    color: #666;
    font-weight: 500;
    min-width: 35px;
  }
  
  .status-text {
    font-size: 0.9rem;
    color: #3B82F6;
    font-weight: 500;
    animation: fadeInOut 2s infinite;
  }
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-10px);
    }
  }
  
  @keyframes fadeInOut {
    0%, 100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }
`;

const LoadingProgressBar = styled.div`
  width: 100%;
  height: 3px;
  background: rgba(59, 130, 246, 0.2);
  border-radius: 2px;
  margin-top: 0.5rem;
  overflow: hidden;
  
  .progress {
    height: 100%;
    background: linear-gradient(90deg, #6EE7B7, #3B82F6, #C084FC, #F472B6, #FACC15 );
    background-size: 200% 100%;
    animation: loading 2s infinite;
    border-radius: 2px;
  }
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ThinkingMessages = styled.div`
  font-size: 0.8rem;
  color: #666;
  font-style: italic;
  margin-top: 0.5rem;
  text-align: center;
`;

const QuickSuggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SuggestionChip = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 2rem;
  font-size: 0.9rem;
  color: #3B82F6;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: linear-gradient(135deg, #3B82F6, #1E40AF);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
    border-color: transparent;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    flex: 1;
    text-align: center;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #64748B;
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
    color: #3B82F6;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: #334155;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  p {
    color: #64748B;
    max-width: 400px;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

function App() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Xin chào! Tôi là chatbot hỗ trợ tra cứu thủ tục hành chính. Bạn có thể hỏi tôi về các thủ tục như đăng ký khai sinh, làm chứng minh nhân dân, giấy phép kinh doanh, v.v.",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sessionId, setSessionId] = useState(() => Math.floor(Math.random() * 10000).toString());
  const [responseTimer, setResponseTimer] = useState(0);
  const [currentThinkingMessage, setCurrentThinkingMessage] = useState('');
  const [isMobileHeaderCompact, setIsMobileHeaderCompact] = useState(false);
  
  const messagesEndRef = useRef(null);
  
  const quickSuggestions = [
    { label: "Tách/hợp thửa đất", value: "tách thửa hoặc hợp thửa đất (mã tthc: 1.012784) (bao gồm trường hợp không thay đổi và có thay đổi người sử dụng đất)" },
    { label: "Cấp lại GCN bị mất", value: "cấp lại giấy chứng nhận do bị mất (mã tthc: 1.012786) (bao gồm mất gcn và mất trang bổ sung)" },
    { label: "Đăng ký biến động QSDĐ", value: "đăng ký biến động quyền sử dụng đất... (chuyển đổi, chuyển nhượng, thừa kế, tặng cho, góp vốn, cho thuê, cho thuê lại...) (mã tthc: 1.013831)" },
    { label: "Đổi tên/thông tin QSDĐ", value: "đăng ký biến động đối với trường hợp đổi tên hoặc thay đổi thông tin... (thay đổi thông tin người sdđ, số hiệu/địa chỉ, hạn chế quyền, giảm diện tích do sạt lở) (mã tthc: 1.013833)" },  
  ];

  const thinkingMessages = React.useMemo(() => [
    "Đang tìm kiếm thông tin...",
    "Đang phân tích câu hỏi của bạn...",
    "Đang tra cứu cơ sở dữ liệu thủ tục...",
    "Đang chuẩn bị câu trả lời chi tiết...",
    "Đang xử lý yêu cầu...",
    "Đang tổng hợp thông tin...",
    "Đang kiểm tra quy định mới nhất...",
    "Đang soạn thảo hướng dẫn...",
    "Một chút nữa thôi...",
    "Gần xong rồi..."
  ], []);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, currentResponse]);

  // Timer effect for response time
  useEffect(() => {
    let interval;
    if (isLoading) {
      setResponseTimer(0);
      interval = setInterval(() => {
        setResponseTimer(prev => prev + 1);
      }, 1000);
    } else {
      setResponseTimer(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);

  // Thinking messages effect
  useEffect(() => {
    let messageInterval;
    if (isLoading && !isStreaming) {
      let messageIndex = 0;
      setCurrentThinkingMessage(thinkingMessages[0]);
      
      messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % thinkingMessages.length;
        setCurrentThinkingMessage(thinkingMessages[messageIndex]);
      }, 3000); // Change message every 3 seconds
    } else {
      setCurrentThinkingMessage('');
    }
    
    return () => {
      if (messageInterval) clearInterval(messageInterval);
    };
  }, [isLoading, isStreaming, thinkingMessages]);

  // Auto-compact header on mobile when chat starts
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768;
      const hasUserMessages = messages.some(msg => msg.isUser);
      setIsMobileHeaderCompact(isMobile && hasUserMessages);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [messages]);

  const generateSessionId = () => {
    return Math.floor(Math.random() * 10000).toString();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  const clearHistory = () => {
    setMessages([
      {
        id: 1,
        text: "Xin chào! Tôi là chatbot hỗ trợ tra cứu thủ tục hành chính. Bạn có thể hỏi tôi về các thủ tục như đăng ký khai sinh, làm chứng minh nhân dân, giấy phép kinh doanh, v.v.",
        isUser: false,
        timestamp: new Date()
      }
    ]);
    setShowSuggestions(true);
    setCurrentResponse('');
    setIsStreaming(false);
  };



  const resetSession = () => {
    setSessionId(generateSessionId());
    clearHistory();
  };

  const sendMessage = async (message = inputValue) => {
    if (!message.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      text: message,
      isUser: true,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsStreaming(true);
    setCurrentResponse('');
    setShowSuggestions(false);
    
    try {
      const response = await fetch('https://autoflow.dxtayninh.cloud/webhook/tthc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic YWRtaW50dGhjOjEyMzQ1NmFA'
        },
        body: JSON.stringify({
          message: message,
          sessionId: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      const processStreamData = (data) => {
        if (data.type === 'item') {
          accumulatedText += data.content;
          setCurrentResponse(accumulatedText);
        } else if (data.type === 'end') {
          setIsStreaming(false);
          setMessages(prev => [...prev, {
            id: Date.now(),
            text: accumulatedText,
            isUser: false,
            timestamp: new Date()
          }]);
          setCurrentResponse('');
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());
        
        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            processStreamData(data);
          } catch (e) {
            // Ignore JSON parse errors for incomplete chunks
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: 'Xin lỗi, đã có lỗi xảy ra. Vui lòng thử lại sau.',
        isUser: false,
        timestamp: new Date()
      }]);
      setIsStreaming(false);
      setCurrentResponse('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  return (
    <AppContainer>
      <Header>
        <h1>
          🏛️ Chatbot Thủ Tục Hành Chính
        </h1>
        {!isMobileHeaderCompact && (
          <p>
            Hỗ trợ tra cứu thông tin thủ tục hành chính 24/7
            {isLoading && (
              <span style={{ color: '#3B82F6', marginLeft: '10px' }}>
                • Đang xử lý ({formatTime(responseTimer)})
              </span>
            )}
          </p>
        )}
        {isMobileHeaderCompact && isLoading && (
          <p style={{ margin: 0, fontSize: '0.7rem' }}>
            Đang xử lý ({formatTime(responseTimer)})
          </p>
        )}
        <HeaderActions>
          <ActionButton onClick={clearHistory} title="Xóa lịch sử chat">
            <IoTrashOutline />
          </ActionButton>
          <ActionButton onClick={resetSession} title="Reset phiên và xóa lịch sử">
            <IoRefreshOutline />
          </ActionButton>
        </HeaderActions>
      </Header>
      
      <ChatContainer>
        <MessagesArea>
          {messages.length === 1 && showSuggestions && (
            <EmptyState>
              <div className="icon">💬</div>
              <h3>Bắt đầu cuộc trò chuyện</h3>
              <p>Chọn một trong các gợi ý dưới đây hoặc nhập câu hỏi của bạn</p>
            </EmptyState>
          )}
          
          {messages.map((message) => (
            <Message key={message.id} isUser={message.isUser}>
              {message.isUser ? (
                message.text
              ) : (
                <MarkdownRenderer isUser={message.isUser}>
                  {message.text}
                </MarkdownRenderer>
              )}
            </Message>
          ))}
          
          {isStreaming && currentResponse && (
            <Message isUser={false}>
              <MarkdownRenderer isUser={false}>
                {currentResponse}
              </MarkdownRenderer>
            </Message>
          )}
          
          {isLoading && !currentResponse && (
            <TypingIndicator>
              <div className="typing-content">
                <div className="dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
                <div className="status-text">
                  {isStreaming ? 'Tôi đang tìm kiếm...' : 'AI đang suy nghĩ'}
                </div>
                <div className="timer">
                  {formatTime(responseTimer)}
                </div>
              </div>
              <LoadingProgressBar>
                <div className="progress"></div>
              </LoadingProgressBar>
              {currentThinkingMessage && (
                <ThinkingMessages>
                  {currentThinkingMessage}
                </ThinkingMessages>
              )}
            </TypingIndicator>
          )}
          
          <div ref={messagesEndRef} />
        </MessagesArea>
        
        {showSuggestions && (
          <QuickSuggestions>
            {quickSuggestions.map((suggestion, index) => (
              <SuggestionChip 
                key={index} 
                onClick={() => handleSuggestionClick(suggestion.value)}
                disabled={isLoading}
              >
                {suggestion.label}
              </SuggestionChip>
            ))}
          </QuickSuggestions>
        )}
        
        <InputContainer isLoading={isLoading}>
          <Input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Nhập câu hỏi về thủ tục hành chính..."
            disabled={isLoading}
          />
          <SendButton onClick={() => sendMessage()} disabled={isLoading || !inputValue.trim()}>
            {isLoading ? '⏳' : <IoSend />}
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </AppContainer>
  );
}

export default App;
